import React, { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { Box, Collapse, Grid, type GridSize, InputLabel, type SvgIconProps, type SxProps, type Theme, Typography } from '@mui/material'

import type { ReadonlyRecord } from '../../../../utils'

import { isTranslation, Translation } from '../../../../domain'

import { TextField, type TextFieldProps } from '../../common'
import { FormattedTranslation } from '../../Internationalization'

type NumberGridSize = Extract<GridSize, number>

const itemCollapsedStyle: SxProps<Theme> = { display: 'none' }

export const GridFormItem: FC<PropsWithChildren<{ expand: boolean | null, size: NumberGridSize }>> = ({ expand, size, children }) => {
    if (expand !== null) {
        children = (
            <Collapse in={expand} unmountOnExit>
                {children}
            </Collapse>
        )
    }

    return (
        <Grid sx={expand === false ? itemCollapsedStyle : undefined} size={{ xs: size }}>
            {children}
        </Grid>
    )
}

export type GridFormEntryProps = Readonly<{
    labelSize?: NumberGridSize
    label?: ReactNode
    endAdornmentSize?: NumberGridSize
    endAdornment?: ReactNode
    expand?: boolean | null
}>

const labelWrapperStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center' }

export const GridFormEntry: FC<PropsWithChildren<GridFormEntryProps>> = ({
    label,
    labelSize = 3,
    endAdornmentSize = 2,
    endAdornment = null,
    expand = null,
    children,
}) => {
    const inputSize = 12 - labelSize - (endAdornment ? endAdornmentSize : 0)

    return (
        <>
            <GridFormItem expand={expand} size={labelSize}>
                <Box sx={labelWrapperStyle}>{label}</Box>
            </GridFormItem>
            <GridFormItem expand={expand} size={inputSize}>
                {children}
            </GridFormItem>
            {endAdornment !== null && (
                <GridFormItem expand={expand} size={endAdornmentSize}>
                    {endAdornment}
                </GridFormItem>
            )}
        </>
    )
}

const labelIconStyle: SxProps<Theme> = { color: 'secondary.main', mr: 1 }
const labelStyle: SxProps<Theme> = { mb: 0, lineHeight: '1.5rem' }

const EntryLabel: FC<PropsWithChildren<{ icon?: FC<SvgIconProps> }>> = ({ icon: Icon, children }) => (
    <>
        {Icon && <Icon sx={labelIconStyle} />}
        {children}
    </>
)

export const EntryInputLabel: FC<{ label: Translation | string, icon?: FC<SvgIconProps>, values?: ReadonlyRecord<string, ReactNode> }> = ({
    label,
    values,
    icon,
}) => (
    <EntryLabel icon={icon}>
        <InputLabel sx={labelStyle}>{isTranslation(label) ? <FormattedTranslation id={label} values={values} /> : label}</InputLabel>
    </EntryLabel>
)

export const EntryInput: FC<
    Pick<TextFieldProps, 'type' | 'placeholder' | 'value' | 'disabled' | 'onChange' | 'startIcon' | 'endIcon' | 'autoComplete' | 'slotProps'>
> = (props) => <TextField fullWidth {...props} />

export const EditableWarning: FC<{ type: 'user' | 'role', canEditAll: boolean, canUpdateMfa?: boolean }> = ({ type, canEditAll, canUpdateMfa = false }) => {
    if (canEditAll) {
        return null
    }
    return (
        <GridFormEntry>
            <Typography data-testid={`${type}-not-editable`} variant="body1">
                <FormattedTranslation id={Translation.NOT_EDITABLE} values={{ canUpdateMfa, type }} />
            </Typography>
        </GridFormEntry>
    )
}
